import { formatDateKey, parseDateTime } from '@/js/core/utils/formatters';
import {
  CALENDAR_EVENT_DISPLAY_MODES,
  calendarEventDateKey,
  calendarEventDateTimeInfo,
  calendarEventEndDateKey,
  calendarEventEndDateTimeInfo,
  calendarEventEndTime,
  calendarEventSortTimestamp,
  calendarEventSourceTimezone,
  calendarEventStartTime,
  calendarEventTimezoneLabel,
} from '@/js/core/utils/calendar-event-time';
import { calendarEventOccurrenceRuns } from '@/js/core/utils/calendar-event-projections';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';
import { textValues, uniqueTextValues } from '@/js/core/utils/event-affiliations';

const OCCURRENCE_GROUPS = [
  { key: 'previous', label: 'Previous Dates' },
  { key: 'upcoming', label: 'Upcoming Dates' },
];

const COURSE_SECTIONS = [
  {
    label: 'Review Type',
    fields: ['review_course_affil'],
  },
  {
    label: 'Course Number',
    fields: [
      'core_stu_affiliation',
      'opt_stu_affiliation',
      'opt_stu_affil_fall',
      'adv_affiliation',
    ],
  },
  {
    label: 'Faculty',
    fields: ['faculty_affiliation'],
  },
];

export function eventModalOccurrenceGroups(
  event,
  { timezone = DEFAULT_TIMEZONE, clock = Date.now() } = {}
) {
  const groupedOccurrences = sortedOccurrences(event).reduce(
    (groups, occurrence) => {
      const groupKey = isPastOccurrence(occurrence, timezone, clock) ? 'previous' : 'upcoming';

      groups[groupKey].push(occurrence);

      return groups;
    },
    { previous: [], upcoming: [] }
  );

  return OCCURRENCE_GROUPS.map(group => {
    const occurrences = groupedOccurrences[group.key];

    return {
      ...group,
      items: dateRangeItems(occurrences, timezone, clock),
    };
  }).filter(group => group.items.length > 0);
}

/**
 * Flat affiliation list for the modal: each group's visible label followed by
 * its items' full names. Suppressed (null-label) groups contribute their
 * items only.
 */
export function eventModalAffiliationItems(event) {
  const groups = Array.isArray(event?.affiliations) ? event.affiliations : [];

  return uniqueTextValues(
    groups.flatMap(group => [
      ...(group?.label ? [group.label] : []),
      ...(group?.items ?? []).map(item => item?.value).filter(Boolean),
    ])
  );
}

export function eventModalCourseSections(event) {
  return COURSE_SECTIONS.map(section => ({
    label: section.label,
    values: uniqueTextValues(section.fields.flatMap(field => textValues(event?.[field]))),
  })).filter(section => section.values.length > 0);
}

/**
 * Narrow an event to the occurrences grouped with the clicked event, which the
 * route slug identifies by Services API id. Returns the event unchanged when
 * nothing matches (event-level slug, stale link).
 *
 * Grouping combines the backend's group_key with contiguous-day runs. group_key
 * includes location, which rightly splits a series alternating rooms into one
 * modal per room, but would also split a single multi-day event that moves room
 * partway through — so consecutive days stay together regardless of location,
 * while gapped dates keep the per-location split.
 *
 * @param {object} event
 * @param {string} occurrenceServiceApiId
 * @param {string} timezone
 * @returns {object}
 */
export function scopeEventToOccurrenceGroup(
  event,
  occurrenceServiceApiId,
  timezone = DEFAULT_TIMEZONE
) {
  const occurrences = Array.isArray(event?.occurrences) ? event.occurrences : [];

  if (!occurrenceServiceApiId || occurrences.length === 0) {
    return event;
  }

  const anchor = occurrences.find(occurrence =>
    occurrenceMatchesServiceApiId(occurrence, occurrenceServiceApiId)
  );

  if (!anchor || !anchor.group_key) {
    return event;
  }

  const runs = calendarEventOccurrenceRuns(occurrences, timezone, {
    skipRunMerging: event.filter_group === 'reviews',
  });
  const scopedOccurrences = occurrenceGroupClosure(occurrences, anchor, runs);
  const scopedLocations = uniqueTextValues(
    scopedOccurrences.flatMap(occurrence => textValues(occurrence.location))
  );
  const hasScopedLocations = scopedLocations.length > 0;

  return {
    ...event,
    occurrences: scopedOccurrences,
    location: hasScopedLocations ? scopedLocations.join(', ') : event.location || '',
    locations: hasScopedLocations ? scopedLocations : event.locations,
  };
}

/**
 * Expands the anchor into every occurrence reachable by a shared group_key or
 * by contiguous-day run membership, to a fixed point.
 *
 * Expanding to closure rather than unioning the anchor's key group with its own
 * run once keeps the result independent of which occurrence was clicked, so two
 * links on the same calendar row open the same set of dates.
 *
 * @param {Array<object>} occurrences
 * @param {object} anchor
 * @param {Array<Array<object>>} runs
 * @returns {Array<object>}
 */
function occurrenceGroupClosure(occurrences, anchor, runs) {
  const scoped = new Set([anchor]);
  const scopedGroupKeys = new Set([anchor.group_key]);
  const runsByOccurrence = new Map();

  runs.forEach(run => run.forEach(occurrence => runsByOccurrence.set(occurrence, run)));

  let expanded = true;

  while (expanded) {
    expanded = false;

    occurrences.forEach(occurrence => {
      if (scoped.has(occurrence)) {
        return;
      }

      const run = runsByOccurrence.get(occurrence) || [];

      if (!scopedGroupKeys.has(occurrence.group_key) && !run.some(member => scoped.has(member))) {
        return;
      }

      scoped.add(occurrence);
      scopedGroupKeys.add(occurrence.group_key);
      expanded = true;
    });
  }

  return occurrences.filter(occurrence => scoped.has(occurrence));
}

function occurrenceMatchesServiceApiId(occurrence, serviceApiId) {
  const target = String(serviceApiId).toLowerCase();
  const ids = [occurrence?.gsd_service_api_id, ...(occurrence?.gsd_service_api_ids || [])];

  return ids.some(id => String(id ?? '').toLowerCase() === target);
}

function sortedOccurrences(event) {
  const occurrences =
    Array.isArray(event?.occurrences) && event.occurrences.length > 0
      ? event.occurrences
      : [event].filter(Boolean);

  return [...occurrences].sort((left, right) => {
    return calendarEventSortTimestamp(left) - calendarEventSortTimestamp(right);
  });
}

function dateRangeItems(occurrences, timezone, clock) {
  if (occurrences.length === 0) {
    return [];
  }

  if (
    occurrences.every(
      occurrence => occurrence.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly
    )
  ) {
    return [dateOnlyRangeItem(occurrences, timezone, clock)];
  }

  return dedupeByDisplay(occurrences.map(occurrence => dateRangeItem(occurrence, timezone, clock)));
}

/**
 * An event booked into two rooms for the same slot arrives as one occurrence per
 * room, and the modal lists location once, aggregated — so those rows would
 * render as the same date and time twice. Occurrences whose schedules actually
 * differ keep their own row.
 *
 * @param {Array<object>} items
 * @returns {Array<object>}
 */
function dedupeByDisplay(items) {
  const seen = new Set();

  return items.filter(item => {
    const signature = [
      item.displayMode,
      item.startDate,
      item.endDate,
      item.start?.iso ?? '',
      item.end?.iso ?? '',
      item.timezoneLabel,
    ].join('|');

    if (seen.has(signature)) {
      return false;
    }

    seen.add(signature);

    return true;
  });
}

function dateOnlyRangeItem(occurrences, timezone, clock) {
  const sorted = [...occurrences].sort((left, right) => {
    return calendarEventSortTimestamp(left) - calendarEventSortTimestamp(right);
  });
  const startDate = sorted.reduce((earliest, occurrence) => {
    const dateKey = calendarEventDateKey(occurrence, timezone);

    return earliest === '' || dateKey < earliest ? dateKey : earliest;
  }, '');
  const endDate = sorted.reduce((latest, occurrence) => {
    const dateKey =
      calendarEventEndDateKey(occurrence, timezone) || calendarEventDateKey(occurrence, timezone);

    return latest === '' || dateKey > latest ? dateKey : latest;
  }, '');

  return {
    key: `${startDate}:${endDate}:date-only`,
    start: null,
    end: null,
    startDate,
    endDate,
    displayMode: CALENDAR_EVENT_DISPLAY_MODES.dateOnly,
    timezoneLabel: '',
    semanticLabel: semanticDateLabel(startDate, endDate, timezone, clock),
  };
}

function dateRangeItem(occurrence, timezone, clock) {
  const start = calendarEventDateTimeInfo(occurrence, timezone);
  const end = calendarEventEndDateTimeInfo(occurrence, timezone);
  const startDate = calendarEventDateKey(occurrence, timezone);
  const endDate = calendarEventEndDateKey(occurrence, timezone) || startDate;
  const displayMode = occurrence.display_mode || CALENDAR_EVENT_DISPLAY_MODES.timed;

  return {
    key: `${calendarEventStartTime(occurrence)}:${calendarEventEndTime(occurrence)}:${occurrence.id || occurrence.gsd_service_api_id || ''}`,
    start,
    end: shouldShowEndTime(start, end) ? end : null,
    startDate,
    endDate,
    displayMode,
    timezoneLabel: calendarEventTimezoneLabel(occurrence, timezone),
    semanticLabel: semanticDateLabel(startDate, endDate, timezone, clock),
  };
}

// The modal shows the full range whenever a real end time exists — start_time_only
// clamps the calendar display, not the modal. Events whose end should stay hidden
// here (e.g. deadlines) already arrive with end collapsed to start by the backend.
function shouldShowEndTime(start, end) {
  if (!start || !end) {
    return false;
  }

  return start.iso !== end.iso;
}

function isPastOccurrence(occurrence, timezone, clock) {
  if (occurrence.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    const todayKey = formatDateKey(clock, timezone);
    const endKey =
      calendarEventEndDateKey(occurrence, timezone) || calendarEventDateKey(occurrence, timezone);

    return Boolean(endKey && todayKey && endKey < todayKey);
  }

  const end = parseDateTime(
    calendarEventEndTime(occurrence),
    calendarEventSourceTimezone(occurrence)
  );

  return end ? end.getTime() <= Number(clock) : false;
}

function semanticDateLabel(startDate, endDate, timezone, clock) {
  const todayKey = formatDateKey(clock, timezone);

  if (!startDate || !endDate || !todayKey) {
    return '';
  }

  if (todayKey >= startDate && todayKey <= endDate) {
    return 'TODAY';
  }

  return nextDateKey(todayKey) === startDate ? 'TOMORROW' : '';
}

function nextDateKey(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day + 1, 12)).toISOString().slice(0, 10);
}
